import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { usersApi } from '../../services/api';
import { User, UserCreate, UserUpdate, UserFormData, PaginatedResponse, FetchUsersParams } from '../../types';

interface UsersState {
  users: PaginatedResponse<User> | null;
  selectedUser: User | null;
  selectedUserIds: Set<number>;
  isLoading: boolean;
  isActionLoading: boolean;
  error: string | null;
}

const initialState: UsersState = {
  users: null,
  selectedUser: null,
  selectedUserIds: new Set(),
  isLoading: false,
  isActionLoading: false,
  error: null,
};

export const fetchUsers = createAsyncThunk(
  'users/fetchUsers',
  async (params: FetchUsersParams = {}, { rejectWithValue }) => {
    try {
      const { page = 1, limit = 10, ...restParams } = params;
      const skip = (page - 1) * limit;
      const response = await usersApi.getUsers({ 
        skip,
        limit,
        ...restParams 
      });
      
      const { data, meta } = response.data;
      return {
        items: data,
        total: meta.total,
        page: meta.page,
        size: meta.per_page,
        pages: meta.pages
      };
    } catch (error: any) {
      if (error.response?.data?.detail) {
        return rejectWithValue(Array.isArray(error.response.data.detail)
          ? error.response.data.detail[0].msg
          : error.response.data.detail);
      }
      return rejectWithValue('Failed to fetch users');
    }
  }
);

export const fetchUser = createAsyncThunk(
  'users/fetchUser',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await usersApi.getUser(id);
      return response.data;
    } catch (error: any) {
      if (error.response?.data?.detail) {
        return rejectWithValue(Array.isArray(error.response.data.detail)
          ? error.response.data.detail[0].msg
          : error.response.data.detail);
      }
      return rejectWithValue('Failed to fetch user details');
    }
  }
);

export const createUser = createAsyncThunk(
  'users/createUser',
  async (userData: UserFormData, { rejectWithValue }) => {
    try {
      const response = await usersApi.createUser(userData);
      return response.data;
    } catch (error: any) {
      if (error.response?.data?.detail) {
        return rejectWithValue(Array.isArray(error.response.data.detail)
          ? error.response.data.detail[0].msg
          : error.response.data.detail);
      }
      return rejectWithValue('Failed to create user');
    }
  }
);

export const updateUser = createAsyncThunk(
  'users/updateUser',
  async ({ id, data }: { id: number; data: UserUpdate }, { rejectWithValue }) => {
    try {
      const response = await usersApi.updateUser(id, data);
      return response.data;
    } catch (error: any) {
      if (error.response?.data?.detail) {
        return rejectWithValue(Array.isArray(error.response.data.detail)
          ? error.response.data.detail[0].msg
          : error.response.data.detail);
      }
      return rejectWithValue('Failed to update user');
    }
  }
);

export const updateUserStatus = createAsyncThunk(
  'users/updateUserStatus',
  async ({ id, status }: { id: number; status: string }, { rejectWithValue }) => {
    try {
      const response = await usersApi.updateUserStatus(id, status);
      return { id, status, ...response.data };
    } catch (error: any) {
      if (error.response?.data?.detail) {
        return rejectWithValue(Array.isArray(error.response.data.detail)
          ? error.response.data.detail[0].msg
          : error.response.data.detail);
      }
      return rejectWithValue('Failed to update user status');
    }
  }
);

export const deleteUser = createAsyncThunk(
  'users/deleteUser',
  async (userId: number, { rejectWithValue }) => {
    try {
      await usersApi.deleteUser(userId);
      return userId;
    } catch (error: any) {
      if (error.response?.data?.detail) {
        return rejectWithValue(Array.isArray(error.response.data.detail)
          ? error.response.data.detail[0].msg
          : error.response.data.detail);
      }
      return rejectWithValue('Failed to delete user');
    }
  }
);

const usersSlice = createSlice({
  name: 'users',
  initialState,
  reducers: {
    setSelectedUser: (state, action) => {
      state.selectedUser = action.payload;
    },
    clearSelectedUser: (state) => {
      state.selectedUser = null;
    },
    clearError: (state) => {
      state.error = null;
    },
    toggleUserSelection: (state, action) => {
      const userId = action.payload;
      const selectedUserIds = new Set(state.selectedUserIds);
      if (selectedUserIds.has(userId)) {
        selectedUserIds.delete(userId);
      } else {
        selectedUserIds.add(userId);
      }
      state.selectedUserIds = selectedUserIds;
    },
    togglePageSelection: (state, action: { payload: number[] }) => {
      const userIds = action.payload;
      const selectedUserIds = new Set(state.selectedUserIds);
      const allSelected = userIds.every((id: number) => selectedUserIds.has(id));
      
      if (allSelected) {
        userIds.forEach((id: number) => selectedUserIds.delete(id));
      } else {
        userIds.forEach((id: number) => selectedUserIds.add(id));
      }
      state.selectedUserIds = selectedUserIds;
    },
    clearSelectedUsers: (state) => {
      state.selectedUserIds = new Set();
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUsers.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.isLoading = false;
        state.users = action.payload;
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.selectedUser = action.payload;
      })
      .addCase(fetchUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(createUser.pending, (state) => {
        state.isActionLoading = true;
        state.error = null;
      })
      .addCase(createUser.fulfilled, (state, action) => {
        state.isActionLoading = false;
        if (state.users?.items) {
          state.users.items.push(action.payload);
          state.users.total += 1;
        }
      })
      .addCase(createUser.rejected, (state, action) => {
        state.isActionLoading = false;
        state.error = action.payload as string;
      })
      .addCase(updateUser.pending, (state) => {
        state.isActionLoading = true;
        state.error = null;
      })
      .addCase(updateUser.fulfilled, (state, action) => {
        state.isActionLoading = false;
        if (state.users?.items) {
          const index = state.users.items.findIndex(user => user.user_id === action.payload.user_id);
          if (index !== -1) {
            state.users.items[index] = {
              ...state.users.items[index],
              ...action.payload
            };
          }
        }
        if (state.selectedUser?.user_id === action.payload.user_id) {
          state.selectedUser = {
            ...state.selectedUser,
            ...action.payload
          };
        }
      })
      .addCase(updateUser.rejected, (state, action) => {
        state.isActionLoading = false;
        state.error = action.payload as string;
      })
      .addCase(updateUserStatus.pending, (state) => {
        state.isActionLoading = true;
        state.error = null;
      })
      .addCase(updateUserStatus.fulfilled, (state, action) => {
        state.isActionLoading = false;
        if (state.users?.items) {
          const index = state.users.items.findIndex(user => user.user_id === action.payload.id);
          if (index !== -1) {
            state.users.items[index] = {
              ...state.users.items[index],
              ...action.payload
            };
          }
        }
        if (state.selectedUser?.user_id === action.payload.id) {
          state.selectedUser = {
            ...state.selectedUser,
            ...action.payload
          };
        }
      })
      .addCase(updateUserStatus.rejected, (state, action) => {
        state.isActionLoading = false;
        state.error = action.payload as string;
      })
      .addCase(deleteUser.pending, (state) => {
        state.isActionLoading = true;
        state.error = null;
      })
      .addCase(deleteUser.fulfilled, (state, action) => {
        state.isActionLoading = false;
        if (state.users) {
          state.users.items = state.users.items.filter(user => user.user_id !== action.payload);
          state.users.total -= 1;
          
          const newPages = Math.ceil(state.users.total / state.users.size);
          state.users.pages = newPages;
          
          if (state.users.items.length === 0 && state.users.page > 1) {
            state.users.page = Math.min(state.users.page, newPages);
          }
        }
        if (state.selectedUser?.user_id === action.payload) {
          state.selectedUser = null;
        }
      })
      .addCase(deleteUser.rejected, (state, action) => {
        state.isActionLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { 
  setSelectedUser, 
  clearSelectedUser,
  clearError,
  toggleUserSelection,
  togglePageSelection,
  clearSelectedUsers
} = usersSlice.actions;

export default usersSlice.reducer;
